import { fmtDate, fmtMoney2 } from '../lib/fmt';
import { CONFERENCIA_DATE_TOLERANCE_DAYS } from '../lib/nfStatus';
import { MoneyInput } from './inputs/MoneyInput';
import type { ConferenciaStatus, ParcelaConferencia, PedidoOut } from '../types/api';

interface NFConferenciaTableProps {
  pedido: PedidoOut | null;
  parcelasPedidoCount: number;
  faturasCount: number;
  rows: ParcelaConferencia[];
  statusConferencia: ConferenciaStatus;
  totalPedidoParcelas: number;
  totalFaturas: number;
  totalBoletoReal: number;
  onBoletoRealChange: (idx: number, field: 'data' | 'valor', value: string) => void;
  showNfColumn?: boolean;
  competenciaAjustes?: { nfNumero?: string; emissao: string; competencia: string }[];
}

export function NFConferenciaTable({
  pedido,
  parcelasPedidoCount,
  faturasCount,
  rows,
  statusConferencia,
  totalPedidoParcelas,
  totalFaturas,
  totalBoletoReal,
  onBoletoRealChange,
  showNfColumn = false,
  competenciaAjustes = [],
}: NFConferenciaTableProps) {
  const parcelasConferidas = rows.filter(row => row.status === 'conferido').length;
  const parcelasDivergentes = rows.filter(row => row.status === 'divergente').length;
  const parcelasPendentes = rows.filter(row => row.status === 'pendente').length;

  // Coluna "Boleto REAL" só aparece quando há divergência entre NF e pedido,
  // ou quando alguma linha já tem boleto real preenchido (edição de NF existente)
  const showBoletoReal = rows.some(row => !row.nfMatchesPO || row.realData !== '');

  return (
    <div className="mb-4 rounded-xl border border-mbc-line bg-mbc-panel p-3 md:p-4" style={{ borderColor: 'rgba(30,111,214,.35)', background: 'rgba(10,77,158,.06)' }}>
      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-wider text-mbc-bluel">📊 Combinado x NF x Boleto Real</div>
        <div className="text-[10px] text-mbc-mute">{parcelasPedidoCount} parcelas no pedido · {faturasCount} faturas na NF</div>
      </div>

      {!pedido ? (
        <div className="rounded-lg border border-amber-400/25 bg-amber-500/10 p-3 text-sm text-amber-300">
          Selecione um pedido para habilitar a conferência por boleto real.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-mbc-line bg-mbc-panel2 p-3 text-sm text-mbc-mute">
          O pedido selecionado ainda não possui parcelas calculadas e a NF não trouxe faturas extraídas.
        </div>
      ) : (
        <>
          <div
            className={`mb-3 rounded-lg border-l-4 p-3 ${
              statusConferencia === 'conferido'
                ? 'border-emerald-400 bg-emerald-500/10'
                : statusConferencia === 'divergente'
                  ? 'border-rose-400 bg-rose-500/10'
                  : 'border-mbc-mute bg-mbc-mute/10'
            }`}
          >
            <div className={`text-sm font-bold ${statusConferencia === 'conferido' ? 'text-emerald-300' : statusConferencia === 'divergente' ? 'text-rose-300' : 'text-mbc-soft'}`}>
              {statusConferencia === 'conferido' ? '✓ Pedido CONFERIDO' : statusConferencia === 'divergente' ? '⚠ Pedido com DIVERGÊNCIAS' : '⏳ Conferência PENDENTE'}
            </div>
            <div className="mt-0.5 text-xs text-mbc-mute">
              {statusConferencia === 'conferido'
                ? `${parcelasConferidas}/${rows.length} parcelas bateram com o combinado.`
                : statusConferencia === 'divergente'
                  ? `${parcelasDivergentes} parcela(s) fora do combinado · ${parcelasConferidas} ok · ${parcelasPendentes} pendente(s).`
                  : `${parcelasPendentes}/${rows.length} parcelas aguardando preenchimento da data real do boleto.`}
            </div>
          </div>

          <div className="overflow-auto">
            <table className="dt" style={{ minWidth: showBoletoReal ? (showNfColumn ? 1220 : 1120) : (showNfColumn ? 880 : 780) }}>
              <colgroup>
                <col style={{ width: 48 }} />
                <col style={{ width: 128 }} />
                <col style={{ width: 128 }} />
                <col style={{ width: 54 }} />
                {showNfColumn && <col style={{ width: 80 }} />}
                <col style={{ width: 128 }} />
                <col style={{ width: 128 }} />
                <col style={{ width: 54 }} />
                {showBoletoReal && <col style={{ width: 140 }} />}
                {showBoletoReal && <col style={{ width: 118 }} />}
                {showBoletoReal && <col style={{ width: 54 }} />}
                <col style={{ width: 120 }} />
                <col style={{ width: 126 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-center" rowSpan={2}>Parc.</th>
                  <th className="text-center" colSpan={3} style={{ background: 'rgba(30,111,214,.18)' }}>📋 Combinado</th>
                  <th className="text-center" colSpan={showNfColumn ? 4 : 3} style={{ background: 'rgba(244,122,32,.15)' }}>📄 Boleto na NF</th>
                  {showBoletoReal && (
                    <th className="text-center" colSpan={3} style={{ background: 'rgba(34,160,107,.15)' }}>💰 Boleto REAL recebido</th>
                  )}
                  <th className="text-center" rowSpan={2}>Diff vs.</th>
                  <th className="text-center" rowSpan={2}>Status</th>
                </tr>
                <tr>
                  <th style={{ background: 'rgba(30,111,214,.08)' }}>Data</th>
                  <th className="num" style={{ background: 'rgba(30,111,214,.08)' }}>Valor</th>
                  <th className="num" style={{ background: 'rgba(30,111,214,.08)' }}>P...</th>
                  {showNfColumn && <th className="text-center" style={{ background: 'rgba(244,122,32,.08)' }}>NFe</th>}
                  <th style={{ background: 'rgba(244,122,32,.08)' }}>Data</th>
                  <th className="num" style={{ background: 'rgba(244,122,32,.08)' }}>Valor</th>
                  <th className="num" style={{ background: 'rgba(244,122,32,.08)' }}>P...</th>
                  {showBoletoReal && <th style={{ background: 'rgba(34,160,107,.08)' }}>Data ★</th>}
                  {showBoletoReal && <th className="num" style={{ background: 'rgba(34,160,107,.08)' }}>Valor ★</th>}
                  {showBoletoReal && <th className="num" style={{ background: 'rgba(34,160,107,.08)' }}>P...</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const rowBg = row.status === 'conferido'
                    ? 'rgba(34,160,107,.05)'
                    : row.status === 'divergente'
                      ? 'rgba(244,63,94,.07)'
                      : undefined;
                  return (
                    <tr key={row.idx} style={rowBg ? { background: rowBg } : undefined}>
                      <td className="text-center font-bold">{row.numero}</td>
                      <td>{row.pedidoVencimento ? fmtDate(row.pedidoVencimento) : <span className="text-mbc-mute">— (sem parcela)</span>}</td>
                      <td className="num">{row.pedidoValor != null ? fmtMoney2(row.pedidoValor) : '—'}</td>
                      <td className="num text-mbc-mute text-[10px]">{row.pedidoPrazo != null ? `${row.pedidoPrazo}d` : '—'}</td>
                      {showNfColumn && (
                        <td className="text-center font-mono text-[10px] text-mbc-orangel">{row.nfNumero ?? '—'}</td>
                      )}
                      <td>{row.nfVencimento ? fmtDate(row.nfVencimento) : <span className="text-mbc-mute">—</span>}</td>
                      <td className="num">{row.nfValor != null ? fmtMoney2(row.nfValor) : '—'}</td>
                      <td className="num text-mbc-mute text-[10px]">{row.nfPrazo != null ? `${row.nfPrazo}d` : '—'}</td>
                      {showBoletoReal && (
                        row.nfMatchesPO && !row.realData ? (
                          // NF bate com o pedido → sem necessidade de boleto real manual
                          <td colSpan={3} className="text-center">
                            <span className="badge bg-emerald-500/15 text-emerald-300 text-[10px]">✓ NF = Combinado</span>
                          </td>
                        ) : (
                          <>
                            <td>
                              <input
                                type="date"
                                value={row.realData}
                                onChange={e => onBoletoRealChange(row.idx, 'data', e.target.value)}
                                className="w-full rounded border border-mbc-line bg-mbc-panel2 px-2 py-1 text-xs text-white focus:border-mbc-orange focus:outline-none"
                                title="Data efetiva do boleto recebido"
                              />
                            </td>
                            <td>
                              <MoneyInput
                                value={row.realValor ?? 0}
                                onChange={n => onBoletoRealChange(row.idx, 'valor', String(n))}
                                className="w-full rounded border border-mbc-line bg-mbc-panel2 px-2 py-1 text-right text-xs text-white focus:border-mbc-orange focus:outline-none"
                                title="Valor efetivo do boleto recebido"
                              />
                            </td>
                            <td className="num text-mbc-mute text-[10px]">{row.realPrazo != null ? `${row.realPrazo}d` : '—'}</td>
                          </>
                        )
                      )}
                      <td className="text-center">
                        {row.diffDias == null ? (
                          <span className="text-mbc-mute text-[10px]">—</span>
                        ) : Math.abs(row.diffDias) <= CONFERENCIA_DATE_TOLERANCE_DAYS ? (
                          <span className="badge bg-emerald-500/20 text-emerald-300">✓ OK</span>
                        ) : row.diffDias > 0 ? (
                          <span className="badge bg-rose-500/25 text-rose-300" title={`Boleto vence ${row.diffDias}d após combinado`}>+{row.diffDias}d</span>
                        ) : (
                          <span className="badge bg-amber-500/20 text-amber-300" title={`Boleto vence ${Math.abs(row.diffDias)}d antes do combinado`}>{row.diffDias}d</span>
                        )}
                      </td>
                      <td className="text-center">
                        {row.status === 'conferido' ? (
                          <span className="badge bg-emerald-500/25 text-emerald-300">✓ conferido</span>
                        ) : row.status === 'divergente' ? (
                          <span className="badge bg-rose-500/25 text-rose-300">⚠ divergente</span>
                        ) : (
                          <span className="badge bg-mbc-mute/20 text-mbc-mute">⏳ aguardando</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: '2px solid #22324F' }}>
                  <td className="text-right text-mbc-mute text-xs" colSpan={2}>Totais:</td>
                  <td className="num font-bold text-white">{fmtMoney2(totalPedidoParcelas)}</td>
                  <td />
                  {showNfColumn && <td />}
                  <td />
                  <td className="num font-bold text-white">{fmtMoney2(totalFaturas)}</td>
                  {showBoletoReal ? (
                    <>
                      <td />
                      <td />
                      <td className="num font-bold text-white">{totalBoletoReal > 0 ? fmtMoney2(totalBoletoReal) : '—'}</td>
                      <td />
                    </>
                  ) : (
                    <td />
                  )}
                  <td className="text-center text-[10px] text-mbc-mute">{totalPedidoParcelas > 0 && totalBoletoReal > 0 ? `${((totalBoletoReal / totalPedidoParcelas) * 100).toFixed(1)}%` : '—'}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
          {showBoletoReal && (
            <div className="mt-2 text-[10px] text-mbc-mute">
              💡 <b>★ Boleto REAL</b>: data e valor que efetivamente chegaram no boleto recebido. Preencha parcela por parcela; a NF só fica conferida quando o boleto real bate com o combinado do pedido.
            </div>
          )}
          {!showBoletoReal && (
            <div className="mt-2 text-[10px] text-emerald-400/70">
              ✓ Todas as faturas da NF batem com as parcelas do pedido — conferência automática sem necessidade de boleto real.
            </div>
          )}
          {competenciaAjustes.length > 0 && (
            <div className="mt-2 rounded-lg border border-mbc-bluel/25 bg-mbc-bluel/10 px-3 py-2 text-[10px] text-mbc-bluel">
              ℹ️ Prazo do <b>Boleto NF-e</b> calculado pela data de competência (o combinado segue pela emissão):{' '}
              {competenciaAjustes
                .map(a => `${a.nfNumero ? `NF ${a.nfNumero} — ` : ''}competência ${fmtDate(a.competencia)} (emissão ${fmtDate(a.emissao)})`)
                .join(' · ')}
            </div>
          )}
        </>
      )}
    </div>
  );
}
